import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import type { Brand, Org } from '@flipdish/orgmanagement';
import { useInfiniteQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { getTranslate } from 'react-localize-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { BRAND_SEARCH_PREFIX } from '../../../../../components/RMS/OrgAndBrandSelector/constants';
import { useBrandSwitchPath } from '../../../../../components/RMS/OrgAndBrandSelector/useBrandSwitchPath';
import {
  getBrandsForOrg,
  getOrgs,
  getOrgsKey,
} from '../../../../../components/RMS/organisation.services';
import { setCurrentOrg } from '../../../../../components/RMS/rms.actions';
import {
  getAllBrands,
  getAllBrandsKey,
} from '../../../../../components/Settings/Brands/brands.services';
import { openInNewTab as openPathInNewTab } from '../../../../../helpers/navigation.utils';
import { notifyError } from '../../../../Notify/actions';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_PARAM = 'orgSearch';

/** A row of either list, so the panel renders orgs and brands the same way. */
export type SwitcherItem = {
  id: string;
  name: string;
  logoImageUrl?: string;
  isArchived?: boolean;
};

type Options = {
  /** Called once the user has chosen another organisation, or the one they are in, so the shell can show its summary. */
  onOrganisationSwitched: () => void;
  /** Called once the user has chosen another brand (`Brand:` search), so the shell can close the panel where it covers the page. */
  onBrandSwitched: () => void;
};

const isBrandSearch = (term: string) =>
  term.toLowerCase().startsWith(BRAND_SEARCH_PREFIX.toLowerCase());

/** The text after the `Brand:` prefix, trimmed. */
const brandTermOf = (term: string) => term.slice(BRAND_SEARCH_PREFIX.length).trim();

const toItem = (entity: Org | Brand, id: string | undefined): SwitcherItem => ({
  id: id ?? '',
  name: entity.name,
  logoImageUrl: entity.logoImageUrl,
  isArchived: entity.isArchived,
});

const nextPageOf = (totalCount: number | undefined, loadedPages: number) =>
  loadedPages * PAGE_SIZE < (totalCount ?? 0) ? loadedPages + 1 : undefined;

/**
 * Data and selection logic of the organisation switcher: a debounced search over the paginated
 * org list (or over every brand when the query starts with `Brand:`), the recently viewed orgs,
 * and switching to an org's first brand. The search term is mirrored into the `orgSearch` URL
 * param, which also seeds it, so the term survives a trip back to the summary. Markup lives in
 * `SwitchOrganisationPanel`.
 *
 * @param options.onOrganisationSwitched Called once the user has chosen an organisation, so the shell can show its summary.
 * @param options.onBrandSwitched Called once the user has chosen a brand, so the shell can close a covering panel.
 * @returns The search state, the item lists with their loading flags, and the `selectItem` action.
 */
export const useOrganisationSwitcher = ({ onOrganisationSwitched, onBrandSwitched }: Options) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  // The same page under the new brand where the page is allow-listed, otherwise its home (ADR 0009).
  const pathForBrand = useBrandSwitchPath();
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const currentOrg = useAppSelector((state) => state.rms.currentOrg);

  // `?orgSearch=` deep link, kept in step with the field so the search survives a reload.
  const initialSearch = new URLSearchParams(location?.search ?? '').get(SEARCH_PARAM) ?? '';
  const [search, setSearch] = useState<string>(initialSearch);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearch.trim());
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  // Read inside `selectItem`, where state would be stale within the same tick.
  const switchingRef = useRef<boolean>(false);

  const historyRef = useRef(history);
  historyRef.current = history;
  // The page the switcher opened on. A pending term belongs to it, not to a page switched to meanwhile.
  const mountPathnameRef = useRef(location?.pathname);

  const debouncedCommitSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value.trim());
        const current = historyRef.current.location;
        const params = new URLSearchParams(current?.search ?? '');
        if (value.trim()) params.set(SEARCH_PARAM, value.trim());
        else params.delete(SEARCH_PARAM);
        // Spread so the hash and router state survive the replace.
        historyRef.current.replace({ ...current, search: params.toString() });
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  // Going back to the summary unmounts the switcher; the pending term still reaches the URL, which seeds the
  // field on return. After a switch the page has changed, so a term typed just before is dropped instead.
  useEffect(
    () => () => {
      if (historyRef.current.location.pathname === mountPathnameRef.current) {
        debouncedCommitSearch.flush();
      } else {
        debouncedCommitSearch.cancel();
      }
    },
    [debouncedCommitSearch]
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    debouncedCommitSearch(value);
  };

  const brandMode = isBrandSearch(searchTerm);
  const brandTerm = brandMode ? brandTermOf(searchTerm) : '';

  const orgsQuery = useInfiniteQuery({
    queryKey: [getOrgsKey, 'switcher', searchTerm],
    queryFn: ({ pageParam }) =>
      getOrgs({ page: pageParam, pageSize: PAGE_SIZE, idOrNameContains: searchTerm }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => nextPageOf(lastPage.data?.totalCount, pages.length),
    enabled: !brandMode,
    retry: 1,
  });

  const brandsQuery = useInfiniteQuery({
    queryKey: [getAllBrandsKey, 'switcher', brandTerm],
    queryFn: ({ pageParam }) =>
      getAllBrands({ page: pageParam, pageSize: PAGE_SIZE, idOrNameContains: brandTerm }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => nextPageOf(lastPage.data?.totalCount, pages.length),
    enabled: brandMode,
    retry: 1,
  });

  const activeQuery = brandMode ? brandsQuery : orgsQuery;

  useEffect(() => {
    if (activeQuery.error) {
      dispatch(notifyError({ message: 'Something_went_wrong', translate: true }));
    }
  }, [activeQuery.error, dispatch]);

  // The org the user is in belongs in the summary panel, not in a list of orgs to switch to.
  const items = useMemo<SwitcherItem[]>(() => {
    if (brandMode) {
      return (
        brandsQuery.data?.pages.flatMap((page) =>
          (page.data?.data ?? []).map((brand) => toItem(brand, brand.brandId))
        ) ?? []
      );
    }
    return (
      orgsQuery.data?.pages
        .flatMap((page) => page.data?.data ?? [])
        .filter((org) => org.orgId !== currentOrg?.orgId)
        .map((org) => toItem(org, org.orgId)) ?? []
    );
  }, [brandMode, brandsQuery.data, orgsQuery.data, currentOrg?.orgId]);

  // Recents only make sense without a search, and never include the org the user is already in. The API
  // returns them newest first and already caps them (rms.core `orgSwitcherCache.maxRecentOrgs`).
  const recentItems = useMemo<SwitcherItem[]>(() => {
    if (searchTerm) return [];
    const recents = orgsQuery.data?.pages[0]?.data?.recentOrgs ?? [];
    return recents
      .filter((org) => org.orgId !== currentOrg?.orgId)
      .map((org) => toItem(org, org.orgId));
  }, [orgsQuery.data, searchTerm, currentOrg?.orgId]);

  /** Lands on the brand's home page. In a new tab the panel is left as it is. */
  const goToBrand = (brandId: string, openInNewTab: boolean, onSwitched: () => void) => {
    const path = pathForBrand(brandId);
    if (openInNewTab) {
      openPathInNewTab(path);
      return;
    }
    dispatch(setCurrentOrg());
    history.push(path);
    onSwitched();
  };

  /** Switches to the chosen brand, or to the chosen org's first brand, in a new tab when asked. */
  const selectItem = async (item: SwitcherItem, openInNewTab = false) => {
    if (switchingRef.current) return;
    if (brandMode) {
      if (item.id) goToBrand(item.id, openInNewTab, onBrandSwitched);
      return;
    }
    if (!item.id) {
      dispatch(
        notifyError({
          message: translate('No_orgId_found_for_org_name', { name: item.name }) as string,
          translate: false,
        })
      );
      return;
    }
    // The org the user is in is the summary, so show it rather than switch to nothing.
    if (item.id === currentOrg?.orgId) {
      onOrganisationSwitched();
      return;
    }
    switchingRef.current = true;
    setIsSwitching(true);
    try {
      const response = await getBrandsForOrg(item.id);
      const brandId = response?.data?.data?.[0]?.brandId;
      if (!brandId) {
        dispatch(
          notifyError({
            message: translate('No_brands_found', { orgId: item.id }) as string,
            translate: false,
          })
        );
        return;
      }
      goToBrand(brandId, openInNewTab, onOrganisationSwitched);
    } catch (e) {
      console.error(e);
      dispatch(notifyError({ message: 'Something_went_wrong', translate: true }));
    } finally {
      switchingRef.current = false;
      setIsSwitching(false);
    }
  };

  return {
    /** The search field's value. */
    search,
    /** The debounced term the list was loaded for. */
    searchTerm,
    /** Whether the list shows brands across every org (`Brand:` prefix) rather than orgs. */
    brandMode,
    /** The brand search without its prefix, for the empty message. */
    brandTerm,
    handleSearch,
    items,
    recentItems,
    isLoading: activeQuery.isPending,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
    hasNextPage: activeQuery.hasNextPage,
    fetchNextPage: activeQuery.fetchNextPage,
    isSwitching,
    selectItem,
  };
};

export type OrganisationSwitcherState = ReturnType<typeof useOrganisationSwitcher>;
